/**
 * Tier 1 genomic-reference registry. One entry per pathogen catalog
 * record (currently 14: see @biomax/plugin-pathogen-database).
 *
 * Accession IDs are stable identifiers from NCBI / UniProt; the
 * sequences themselves are fetched on-demand via the v0.3 catalog
 * fetcher or bundled at Tier 2 (also v0.3).
 */

import type { GenomicReference } from './types.js';

export const GENOME_REGISTRY: Record<string, GenomicReference> = {
  'sars-cov-2-wildtype': {
    pathogenId: 'sars-cov-2-wildtype',
    refseq: 'NC_045512',
    ncbiTaxId: 2697049,
    genomeLengthBp: 29903,
    uniprot: [
      { protein: 'spike-glycoprotein', id: 'P0DTC2' },
      { protein: 'nucleocapsid', id: 'P0DTC9' },
      { protein: 'orf1ab-polyprotein', id: 'P0DTD1' },
    ],
    tier2Bundled: true,
  },
  'sars-cov-2-omicron-ba5': {
    pathogenId: 'sars-cov-2-omicron-ba5',
    // Omicron variants: variant-fingerprint catalog (Tier 3) against
    // the wild-type RefSeq is the v0.5 plan; no separate FASTA bundled.
    ncbiTaxId: 2697049,
    genomeLengthBp: 29903,
    tier2Bundled: false,
  },
  'sars-cov-2-jn1': {
    pathogenId: 'sars-cov-2-jn1',
    ncbiTaxId: 2697049,
    genomeLengthBp: 29903,
    tier2Bundled: false,
  },
  'influenza-a-h3n2': {
    pathogenId: 'influenza-a-h3n2',
    // Influenza segments live in NCBI Influenza Virus Resource +
    // GISAID EpiFlu — Tier 2 bundling deferred to v0.4 with the
    // Influenza-specific fetcher.
    ncbiTaxId: 119210,
    genomeLengthBp: 13588,
    uniprot: [{ protein: 'hemagglutinin', id: 'P03437' }, { protein: 'neuraminidase', id: 'P03468' }],
    tier2Bundled: false,
  },
  'influenza-a-h1n1pdm09': {
    pathogenId: 'influenza-a-h1n1pdm09',
    ncbiTaxId: 641809,
    genomeLengthBp: 13588,
    tier2Bundled: false,
  },
  'influenza-b-victoria': {
    pathogenId: 'influenza-b-victoria',
    ncbiTaxId: 11520,
    genomeLengthBp: 14548,
    tier2Bundled: false,
  },
  'rsv': {
    pathogenId: 'rsv',
    // RSV-A reference (subgroup A long strain).
    refseq: 'NC_038235',
    ncbiTaxId: 11250,
    genomeLengthBp: 15222,
    tier2Bundled: true,
  },
  'measles': {
    pathogenId: 'measles',
    refseq: 'NC_001498',
    ncbiTaxId: 11234,
    genomeLengthBp: 15894,
    uniprot: [{ protein: 'hemagglutinin', id: 'P08362' }, { protein: 'fusion-glycoprotein', id: 'P08361' }],
    tier2Bundled: true,
  },
  'pertussis': {
    pathogenId: 'pertussis',
    // Bordetella pertussis Tohama I full genome is 4 Mb — large for
    // bundle; deferred to a separate large-genome handling pass in
    // v0.4 (compressed FASTA with on-demand decompression).
    ncbiTaxId: 520,
    genomeLengthBp: 4086189,
    uniprot: [{ protein: 'pertussis-toxin-subunit-1', id: 'P04977' }],
    tier2Bundled: false,
  },
  'dengue-denv1': {
    pathogenId: 'dengue-denv1',
    refseq: 'NC_001477',
    ncbiTaxId: 11053,
    genomeLengthBp: 10735,
    uniprot: [{ protein: 'envelope-E', id: 'P17763' }],
    tier2Bundled: true,
  },
  'ebola-zaire': {
    pathogenId: 'ebola-zaire',
    refseq: 'NC_002549',
    ncbiTaxId: 186538,
    genomeLengthBp: 18959,
    uniprot: [{ protein: 'glycoprotein', id: 'Q05320' }, { protein: 'nucleoprotein', id: 'P18272' }],
    tier2Bundled: true,
  },
  'marburg': {
    pathogenId: 'marburg',
    refseq: 'NC_001608',
    ncbiTaxId: 11269,
    genomeLengthBp: 19111,
    uniprot: [{ protein: 'glycoprotein', id: 'P35253' }],
    tier2Bundled: true,
  },
  'mpxv-clade-iib': {
    pathogenId: 'mpxv-clade-iib',
    // MPXV clade IIb 2022 representative (Pango B.1).
    refseq: 'NC_063383',
    ncbiTaxId: 10244,
    genomeLengthBp: 197000,
    tier2Bundled: true,
  },
  'nipah': {
    pathogenId: 'nipah',
    // Nipah virus reference (Malaysia isolate).
    refseq: 'NC_002728',
    ncbiTaxId: 121791,
    genomeLengthBp: 18246,
    uniprot: [{ protein: 'glycoprotein-G', id: 'Q9IH62' }, { protein: 'fusion-F', id: 'Q9IH63' }],
    tier2Bundled: true,
  },
};
